import { push } from '@lagunovsky/redux-react-router'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { Button, Dialog, DialogActions, DialogContent, Grid, Stack, Typography } from '@mui/material'
import ColorfulCard from '../../components/card/ColorfulCard'
import DtoEvent from '../../events/dto/DtoEvent'
import EventsAction from '../../events/actions/EventsAction'
import EventCard from '../../station/components/event/EventCard'
import { getEventColor } from '../../utils/ColorUtil'
import { getSandreList } from '../../utils/StoreUtils'
import { EVENT_TYPES_CODES } from 'events/constants/EventsConstants'
import { fromPairs } from 'lodash'
import { DefaultDialogTitle } from 'components/styled/Dialog'

const CampaignEventsModal = ({
    events = [],
    isOpen,
    onClose,
    stationType = '',
    campaignCode = '',
    campaignName,
    campaignType = 2,
    objectives = [],
    date,
    strDate,
    stationLabel,
    stationId,
}) => {
    const dispatch = useDispatch()

    const sandreCodes = useSelector(store => store.ReferencialReducer.sandreCodes)

    const [causesByEvent, setCausesByEvent] = useState({})

    const eventIds = events.map(e => e.id).join(',')

    useEffect(() => {
        Promise.all(events.map(e => EventsAction.fetchStationCauseIntervention(stationType, stationId, e.id)
            .then((causes = []) => [e.id, causes])
            .catch(() => [e.id, []])))
            .then(entries => setCausesByEvent(fromPairs(entries)))
    }, [eventIds])

    const getCausesLabel = eventId => (causesByEvent[eventId] ?? []).map(({ interventionId, interventionType }) => {
        const sandre = getSandreList(sandreCodes, `CAUSE_INTERVENTION.${interventionType}`).find(s => s.code === interventionId)
        return sandre?.name ?? `[${interventionId}]`
    }).join(', ')

    const objectivesParam = objectives.length ? `&objectives=${objectives.join(',')}` : ''

    const onCreate = eventTypeCode => dispatch(push(`/station/${stationType}/${stationId}/event/0?date=${date}&campaignCode=${campaignCode}&campaignType=${eventTypeCode}${objectivesParam}`))

    const onCreateNotDone = () => dispatch(push(`/station/${stationType}/${stationId}/event/0?date=${date}&campaignCode=${campaignCode}&campaignType=${EVENT_TYPES_CODES.NOT_DONE}`))

    const campaignEventType = campaignType === 1 ? EVENT_TYPES_CODES.PREVENTIVE
        : campaignType === 2 ? EVENT_TYPES_CODES.CURATIVE
            : EVENT_TYPES_CODES.COMMENT

    return (
        <Dialog open={isOpen} fullWidth maxWidth='md'>
            <DefaultDialogTitle title={i18n.events} onClose={onClose} />
            <DialogContent>
                <Stack spacing={1}>
                    {
                        events.map(e => {
                            const causesLabel = getCausesLabel(e.id)
                            return (
                                <ColorfulCard color={getEventColor(e.eventType)} key={`event_${e.id}`}>
                                    <Stack>
                                        <EventCard
                                            event={e}
                                            small
                                            link={`/station/${stationType}/${stationId}/event/`}
                                            showStationInfo={['productionUnit', 'distributionUnit', 'installation'].includes(stationType)}
                                            stationType={stationType}
                                            oppositeTypeContent={causesLabel && (
                                                <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
                                                    <Typography fontWeight='bold'>{`${i18n.causeIntervention} :`}</Typography>
                                                    {causesLabel}
                                                </Stack>
                                            )}
                                        />
                                    </Stack>
                                </ColorfulCard>
                            )
                        })
                    }
                    <Grid container spacing={1} alignItems='center'>
                        <Typography>{i18n.wantToEnterEventOnStation}</Typography>
                        <Grid size={12}>
                            <Stack direction='row' spacing={0.5}>
                                <Typography fontWeight='bold'>{`${i18n.campaign} :`}</Typography>
                                {campaignName ?? ''}
                            </Stack>
                        </Grid>
                        <Grid size={12}>
                            <Stack direction='row' spacing={0.5}>
                                <Typography fontWeight='bold'>{`${i18n.station} :`}</Typography>
                                {stationLabel ?? ''}
                            </Stack>
                        </Grid>
                        <Grid size={12}>
                            <Stack direction='row' spacing={0.5}>
                                <Typography fontWeight='bold'>{`${i18n.date} :`}</Typography>
                                {strDate ?? ''}
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={onCreateNotDone}>
                    {i18n.notDoneIntervention}
                </Button>
                <Button variant='contained' onClick={() => onCreate(campaignEventType)}>
                    {i18n.new}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

CampaignEventsModal.propTypes = {
    events: PropTypes.arrayOf(PropTypes.instanceOf(DtoEvent)),
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    stationType: PropTypes.string,
    date: PropTypes.number,
    strDate: PropTypes.string,
    stationLabel: PropTypes.string,
    stationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    campaignCode: PropTypes.number,
    campaignName: PropTypes.string,
    campaignType: PropTypes.number,
    objectives: PropTypes.arrayOf(PropTypes.number),
}

export default CampaignEventsModal
